import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Nav, Row, Alert } from "react-bootstrap";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { applyActionCode, getAuth, sendEmailVerification } from 'firebase/auth';
import { useAuth } from "../utils/AuthContext";
import pageSvg from "../assets/svg/mailbox.svg";
import logo from "../assets/img/brandimg.png";

export default function VerifyAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [verificationState, setVerificationState] = useState({
    isLoading: false,
    isSuccess: false,
    error: null,
    message: null
  });

  document.body.classList.remove("sidebar-show");

  useEffect(() => {
    const verifyEmail = async () => {
      const oobCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');

      // If no code or wrong mode, show the default verification needed view
      if (!oobCode || mode !== 'verifyEmail') {
        setVerificationState({
          isLoading: false,
          isSuccess: false,
          error: null,
          message: "Please check your email for the verification link."
        });
        return;
      }

      setVerificationState({ isLoading: true, isSuccess: false, error: null, message: null });

      try {
        const auth = getAuth();
        // Check if user is signed in
        const currentUser = auth.currentUser;

        await applyActionCode(auth, oobCode);

        // If user is signed in, reload their token to reflect email verification
        if (currentUser) {
          await currentUser.reload();
        }

        setVerificationState({
          isLoading: false,
          isSuccess: true,
          error: null,
          message: null
        });
      } catch (error) {
        console.error('Error verifying email:', error);
        let errorMessage;

        switch (error.code) {
          case 'auth/invalid-action-code':
            errorMessage = 'The verification link is invalid. This can happen if the link has already been used or has expired.';
            break;
          case 'auth/user-not-found':
            errorMessage = 'The user account associated with this verification link was not found.';
            break;
          case 'auth/expired-action-code':
            errorMessage = 'The verification link has expired. Please request a new one.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'The email address associated with this verification link is invalid.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'This user account has been disabled. Please contact support.';
            break;
          default:
            errorMessage = 'Failed to verify email. Please try again or request a new verification link.';
        }

        setVerificationState({
          isLoading: false,
          isSuccess: false,
          error: errorMessage,
          message: null
        });
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleResendVerification = async () => {
    if (!currentUser) {
      setVerificationState({
        isLoading: false,
        isSuccess: false,
        error: "Please sign in first to resend the verification email.",
        message: null
      });
      return;
    }

    if (currentUser.emailVerified) {
      setVerificationState({
        isLoading: false,
        isSuccess: false,
        error: null,
        message: "Your email is already verified!"
      });
      return;
    }

    try {
      setVerificationState({ isLoading: true, isSuccess: false, error: null, message: null });
      await sendEmailVerification(currentUser);
      setVerificationState({
        isLoading: false,
        isSuccess: false,
        error: null,
        message: "A new verification email has been sent. Please check your inbox and spam folder."
      });
    } catch (error) {
      console.error('Error sending verification email:', error);
      let errorMessage;

      switch (error.code) {
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please wait a while before requesting another verification email.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'User account not found. Please sign in again.';
          break;
        default:
          errorMessage = 'Failed to send verification email. Please try again later.';
      }

      setVerificationState({
        isLoading: false,
        isSuccess: false,
        error: errorMessage,
        message: null
      });
    }
  };

  // If verification was successful, show success message and redirect option
  if (verificationState.isSuccess) {
    return (
      <div className="page-auth">
        <div className="header">
          <Container>
            <img src={logo} alt="logo" className="header-logo" style={{ width: '170px', height: '170px' }} />
          </Container>
        </div>

        <div className="content">
          <Container>
            <Card className="card-auth">
              <Card.Body className="text-center">
                <div className="mb-5">
                  <object type="image/svg+xml" data={pageSvg} className="w-50" aria-label="svg image"></object>
                </div>
                <Card.Title>Email Verified Successfully!</Card.Title>
                <Card.Text className="mb-5">Your email has been successfully verified. You can now access all features of your account.</Card.Text>

                <Button variant="primary" onClick={() => navigate('/')}>
                  Go to Dashboard
                </Button>
              </Card.Body>
            </Card>
          </Container>
        </div>
      </div>
    );
  }

  // Show loading state
  if (verificationState.isLoading) {
    return (
      <div className="page-auth">
        <div className="header">
          <Container>
            <img src={logo} alt="logo" className="header-logo" style={{ width: '170px', height: '170px' }} />

          </Container>
        </div>

        <div className="content">
          <Container>
            <Card className="card-auth">
              <Card.Body className="text-center">
                <div className="mb-5">
                  <object type="image/svg+xml" data={pageSvg} className="w-50" aria-label="svg image"></object>
                </div>
                <Card.Title>Verifying your email...</Card.Title>
                <Card.Text className="mb-5">Please wait while we verify your email address.</Card.Text>
              </Card.Body>
            </Card>
          </Container>
        </div>
      </div>
    );
  }

  // Default view (including error state)
  return (
    <div className="page-auth">
      <div className="header">
        <Container>
          <img src={logo} alt="logo" className="header-logo" style={{ width: '170px', height: '170px' }} />
        </Container>
      </div>

      <div className="content">
        <Container>
          <Card className="card-auth">
            <Card.Body className="text-center">
              <div className="mb-5">
                <object type="image/svg+xml" data={pageSvg} className="w-50" aria-label="svg image"></object>
              </div>
              <Card.Title>Verify your email address</Card.Title>
              <Card.Text className="mb-5">Please check your email and click the verify button or link to verify your account.</Card.Text>

              {verificationState.error && (
                <Alert variant="danger" className="mb-4">
                  {verificationState.error}
                </Alert>
              )}

              {verificationState.message && (
                <Alert variant="success" className="mb-4">
                  {verificationState.message}
                </Alert>
              )}

              <Row className="g-2 g-sm-3">
                <Col sm><Button variant="primary" onClick={handleResendVerification}>Resend Verification</Button></Col>
                <Col sm><Button variant="secondary" onClick={() => navigate('/auth/signin')}>Back to Sign In</Button></Col>
              </Row>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </div>
  );
}